// Live hall occupancy heatmap. Plots the true seat coordinates per level and
// colours each seat by state: confirmed (crimson), pending pre-reservation
// (gold), held (violet), free (muted), closed (faint). A glanceable "salon
// haritası" — not interactive.

import { LEVEL_LABELS_TR, type Level, type SeatDto } from '@okn/shared';

const LEVEL_ORDER: Level[] = ['ground', 'side_balcony_left', 'side_balcony_right', 'upper_balcony'];

export type OccupancySets = {
  paidIds: Set<string>;
  pendingIds: Set<string>;
  heldIds: Set<string>;
};

type Props = OccupancySets & { allSeats: SeatDto[]; height?: number };

function seatStyle(s: SeatDto, sets: OccupancySets): { fill: string; opacity: number; r: number } {
  if (!s.isSellable) return { fill: 'var(--seat-closed)', opacity: 0.55, r: 1.6 };
  if (sets.paidIds.has(s.id)) return { fill: 'var(--accent)', opacity: 1, r: 2.4 };
  if (sets.pendingIds.has(s.id)) return { fill: 'var(--gold)', opacity: 1, r: 2.3 };
  if (sets.heldIds.has(s.id)) return { fill: 'var(--seat-held)', opacity: 0.9, r: 2.2 };
  return { fill: 'var(--seat-available)', opacity: 0.45, r: 1.7 };
}

export function VenueOccupancyMap({ allSeats, paidIds, pendingIds, heldIds }: Props) {
  const sets = { paidIds, pendingIds, heldIds };
  const byLevel = new Map<Level, SeatDto[]>();
  for (const s of allSeats) {
    const arr = byLevel.get(s.level) ?? [];
    arr.push(s);
    byLevel.set(s.level, arr);
  }
  const levels = LEVEL_ORDER.filter((l) => (byLevel.get(l)?.length ?? 0) > 0);

  return (
    <div className="space-y-3">
      {/* Stage marker */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent to-border-strong" />
        <span className="kicker text-accent">Sahne</span>
        <span className="h-px flex-1 bg-gradient-to-l from-transparent to-border-strong" />
      </div>

      {levels.includes('ground') && (
        <LevelPanel level="ground" seats={byLevel.get('ground') ?? []} sets={sets} h={190} />
      )}

      {(levels.includes('side_balcony_left') || levels.includes('side_balcony_right')) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {levels.includes('side_balcony_left') && (
            <LevelPanel
              level="side_balcony_left"
              seats={byLevel.get('side_balcony_left') ?? []}
              sets={sets}
              h={120}
            />
          )}
          {levels.includes('side_balcony_right') && (
            <LevelPanel
              level="side_balcony_right"
              seats={byLevel.get('side_balcony_right') ?? []}
              sets={sets}
              h={120}
            />
          )}
        </div>
      )}

      {levels.includes('upper_balcony') && (
        <LevelPanel
          level="upper_balcony"
          seats={byLevel.get('upper_balcony') ?? []}
          sets={sets}
          h={120}
        />
      )}
    </div>
  );
}

function LevelPanel({
  level,
  seats,
  sets,
  h,
}: {
  level: Level;
  seats: SeatDto[];
  sets: OccupancySets;
  h: number;
}) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let occupied = 0;
  let sellable = 0;
  for (const s of seats) {
    if (s.posX < minX) minX = s.posX;
    if (s.posY < minY) minY = s.posY;
    if (s.posX > maxX) maxX = s.posX;
    if (s.posY > maxY) maxY = s.posY;
    if (s.isSellable) sellable += 1;
    if (sets.paidIds.has(s.id) || sets.pendingIds.has(s.id)) occupied += 1;
  }
  const pad = 12;
  const w = maxX - minX + pad * 2;
  const vh = maxY - minY + pad * 2;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-bg-deep">
      <div className="flex items-baseline justify-between border-b border-border px-3 py-1.5">
        <span className="kicker">{LEVEL_LABELS_TR[level]}</span>
        <span className="mono text-[11px] text-text-muted tnum">
          {occupied}
          <span className="text-text-faint">/{sellable}</span>
        </span>
      </div>
      <svg
        viewBox={`${minX - pad} ${minY - pad} ${w} ${vh}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ height: h }}
        className="block w-full"
        role="img"
        aria-label={`${LEVEL_LABELS_TR[level]}: ${occupied} / ${sellable} koltuk dolu`}
      >
        {seats.map((s) => {
          const st = seatStyle(s, sets);
          return (
            <circle
              key={s.id}
              cx={s.posX}
              cy={s.posY}
              r={st.r}
              fill={st.fill}
              opacity={st.opacity}
            />
          );
        })}
      </svg>
    </div>
  );
}

const LEGEND: Array<{ label: string; color: string }> = [
  { label: 'Onaylandı', color: 'var(--accent)' },
  { label: 'Ön rezervasyon', color: 'var(--gold)' },
  { label: 'Tutuldu', color: 'var(--seat-held)' },
  { label: 'Boş', color: 'var(--seat-available)' },
  { label: 'Kapalı', color: 'var(--seat-closed)' },
];

export function OccupancyLegend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {LEGEND.map((l) => (
        <span
          key={l.label}
          className="inline-flex items-center gap-1.5 text-[11px] text-text-muted"
        >
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: l.color }} />
          {l.label}
        </span>
      ))}
    </div>
  );
}
